import React, { useEffect, useState, Fragment } from "react"
import { GatsbyImage } from "gatsby-plugin-image"
import { useAnimation, motion } from "framer-motion"
import { useInView } from "react-intersection-observer"
import { Link } from "gatsby"
import { PortableText } from "@portabletext/react"
import { getGatsbyImageData } from "gatsby-source-sanity"
import { useStaticQuery, graphql } from "gatsby"
import {
  FiInstagram,
  FiFacebook,
  FiTwitter,
  FiMap,
  FiCalendar,
  FiWifi,
  FiCheck,
  FiSliders,
  FiMoreHorizontal,
} from "react-icons/fi"
import { FaDog } from "react-icons/fa"
import { Listbox, Transition } from "@headlessui/react"

const motionSection = {
  hidden: {
    opacity: 0,
    // y: 20,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 1,
      staggerChildren: 0.15,
      staggerDirection: 1,
      // delay: 0.3,
      ease: [0.16, 1, 0.3, 1],
      // when: "beforeChildren",
    },
  },
}
const motionItems = {
  hidden: {
    opacity: 0,
    y: 20,
    // rotate: 20,
  },
  visible: {
    opacity: 1,
    // right: 0,
    y: 0,
    transition: {
      duration: 1,
      ease: [0.16, 1, 0.3, 1],
    },
  },
}
const motionImage = {
  hidden: {
    scale: 1.1,
    opacity: 0,
    // rotate: 20,
  },
  visible: {
    scale: 1,
    opacity: 1,
    transition: {
      duration: 1,
      // ease: [0.87, 0, 0.13, 1],
    },
  },
}

const underline = {
  hidden: {
    // opacity: 0,
    width: 0,
  },
  visible: {
    opacity: 1,
    // right: 0,
    width: "100%",
    transition: {
      duration: 0.6,
      ease: [0.16, 1, 0.3, 1],
    },
  },
}
const title = {
  hidden: {
    opacity: 0,
    y: 20,
  },
  visible: {
    opacity: 1,
    // right: 0,
    y: 0,
    transition: {
      duration: 2,
      ease: [0.16, 1, 0.3, 1],
    },
  },
}
const desc = {
  hidden: {
    opacity: 0,
    y: 20,
  },
  visible: {
    opacity: 1,
    // right: 0,
    y: 0,
    transition: {
      duration: 2,
      ease: [0.16, 1, 0.3, 1],
    },
  },
}

const locationsLoop = ({ block }) => {
  const data = useStaticQuery(graphql`
    query locationsLoopQuery {
      states: allSanityLocationsState {
        nodes {
          title
          id
        }
      }
      allPosts: allSanityLocations {
        nodes {
          featuredImage {
            asset {
              altText
              gatsbyImageData
            }
          }
          state {
            title
            slug {
              current
            }
          }
          sidebarTitle
          sidebarText
          sidebarAddress
          sidebarAddressURL
          instagram
          twitter
          facebook
          title
          ctaLabel
          ctaUrl
          slug {
            current
            _type
            _key
          }
        }
      }
    }
  `)

  const { heading, text } = block
  const controls = useAnimation()
  const [ref, inView] = useInView()

  useEffect(() => {
    if (inView) {
      controls.start("visible")
    }
  }, [controls, inView])

  const [posts, setPosts] = useState(data.allPosts.nodes)
  const [selectedState, setSelectedState] = useState("All locations")

  const filterPosts = value => {
    setSelectedState(value)
    let posts = data.allPosts.nodes

    if (value !== "All locations") {
      posts = data.allPosts.nodes.filter(post =>
        post.state.title.includes(value)
      )
    }

    setPosts(posts)
  }

  return (
    <motion.section
      ref={ref}
      animate={controls}
      initial="hidden"
      variants={motionSection}
      className="section multicolumn"
    >
      {heading && (
        <div className="module-image-text-carousel__content flex flex-col justify-center mb-8 items-center text-center container max-w-screen-md">
          <h2 className="w-fit h2 mb-6 relative">
            <motion.div variants={title}>{heading}</motion.div>
            <motion.div
              variants={underline}
              className="section-heading__underline bg-orange-400 h-2 w-full absolute -bottom-4 left-0"
            />
          </h2>
          <motion.p variants={desc}>{text}</motion.p>
        </div>
      )}

      <div className="container">
        <motion.div variants={desc} className="mt-8">
          <Listbox value={selectedState} onChange={e => filterPosts(e)}>
            <div className="relative my-8 w-[250px]">
              {/* <Listbox.Label className="flex gap-2 text-xs items-center mb-2">
                <FiSliders /> Filter
              </Listbox.Label> */}
              <Listbox.Button className="relative w-full cursor-default rounded-lg bg-white py-2 pl-3 pr-10 text-left shadow-md focus:outline-none focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-orange-300 sm:text-sm">
                <span className="block truncate">{selectedState}</span>
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                  <FiMoreHorizontal
                    className="h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                </span>
              </Listbox.Button>
              <Transition
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                  <Listbox.Option
                    key="all"
                    className={({ active }) =>
                      `relative cursor-default select-none py-2 pl-10 pr-4 ${
                        active
                          ? "bg-orange-100 text-orange-500"
                          : "text-gray-900"
                      }`
                    }
                    value="All locations"
                  >
                    {({ selected }) => (
                      <>
                        <span
                          className={`block truncate ${
                            selected ? "font-medium" : "font-normal"
                          }`}
                        >
                          All locations
                        </span>
                        {selected ? (
                          <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-amber-600">
                            <FiCheck className="h-5 w-5" aria-hidden="true" />
                          </span>
                        ) : null}
                      </>
                    )}
                  </Listbox.Option>

                  {data.states.nodes.map((option, index) => (
                    <Listbox.Option
                      key={option.id}
                      className={({ active }) =>
                        `relative cursor-default select-none py-2 pl-10 pr-4 ${
                          active
                            ? "bg-orange-100 text-orange-500"
                            : "text-gray-900"
                        }`
                      }
                      value={option.title}
                    >
                      {({ selected }) => (
                        <>
                          <span
                            className={`block truncate ${
                              selected ? "font-medium" : "font-normal"
                            }`}
                          >
                            {option.title}
                          </span>
                          {selected ? (
                            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-amber-600">
                              <FiCheck className="h-5 w-5" aria-hidden="true" />
                            </span>
                          ) : null}
                        </>
                      )}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </Transition>
            </div>
          </Listbox>

          <div className="grid lg:grid-cols-3 gap-5">
            {posts.map((item, index) => {
              // console.log("item", item)
              return (
                <article className="block p-4 rounded-lg shadow-sm shadow-orange-100 bg-white">
                  <Link to={item.slug.current}>
                    <GatsbyImage
                      image={item.featuredImage.asset.gatsbyImageData}
                      alt={item.featuredImage.asset.altText}
                      className=" object-cover w-full h-56 rounded-md"
                    />
                  </Link>

                  <div className="mt-2">
                    <dl>
                      <div>
                        <dt className="sr-only">State</dt>

                        {item.state ? (
                          <dd className="text-xs text-neutral-300 uppercase tracking-wider">
                            {item.state.title}
                          </dd>
                        ) : (
                          ""
                        )}
                      </div>

                      <div>
                        
                        <Link to={item.slug.current}>
                          <h3 className="font-medium text-lg">{item.title}</h3>
                        </Link>
                      </div>
                    </dl>

                    <dl className="grid grid-cols-2 gap-4 items-center mt-6 text-xs">
                      <div className="sm:inline-flex sm:items-center sm:shrink-0">
                        <FiMap className="w-4 h-4 text-orange-500" />
                        <div className="sm:ml-3 mt-1.5 sm:mt-0">
                          <dd className="font-medium">map</dd>
                        </div>
                      </div>

                      <div className="sm:inline-flex sm:items-center sm:shrink-0">
                        <FiCalendar className="w-4 h-4 text-orange-500" />

                        <div className="sm:ml-3 mt-1.5 sm:mt-0">
                          <dd className="font-medium">events</dd>
                        </div>
                      </div>

                      <div className="sm:inline-flex sm:items-center sm:shrink-0">
                        {/* <FaDog className="w-4 h-4 text-orange-500" /> */}

                        <div className="flex gap-2">
                          {item.instagram && (
                            <FiInstagram className="w-4 h-4 text-orange-500" />
                          )}

                          {item.facebook && (
                            <FiFacebook className="w-4 h-4 text-orange-500" />
                          )}
                          {item.twitter && (
                            <FiTwitter className="w-4 h-4 text-orange-500" />
                          )}
                        </div>

                        <div className="sm:ml-3 mt-1.5 sm:mt-0">
                          <dd className="font-medium">Socials</dd>
                        </div>
                      </div>
                      <div className="sm:inline-flex sm:items-center sm:shrink-0">
                        <FiWifi className="w-4 h-4 text-orange-500" />

                        <div className="sm:ml-3 mt-1.5 sm:mt-0">
                          <dd className="font-medium">WiFi</dd>
                        </div>
                      </div>
                    </dl>

                    {/* <a href="" className="btn-primary mt-8">learn more</a> */}
                    <div className="flex justify-between">
                      <Link to={item.slug.current}  className="relative inline-block mt-12 text-sm font-bold text-orange-500">
                      <span className="absolute inset-x-0 bottom-0 transition-transform transform bg-orange-100 h-2/3 hover:scale-110"></span>
                        <span className="relative">Find out more</span>
                      </Link>
               
                      {item.ctaLabel && (
                        <a
                          href={item.ctaUrl}
                          className="relative inline-block mt-12 text-sm font-bold text-orange-500"
                        >
                          <span className="absolute inset-x-0 bottom-0 transition-transform transform bg-orange-100 h-2/3 hover:scale-110"></span>
                          <span className="relative">{item.ctaLabel}</span>
                        </a>
                      )}
                    </div>
                  </div>
                </article>
              )
            })}
          </div>
        </motion.div>
      </div>
    </motion.section>
  )
}

export default locationsLoop
